import React from 'react';
import {
  collapseStructurePreview,
  stepLengthSecs,
  stepZoneColor,
  stepZoneNumber,
  stepTargetRange,
  type ApiStructBlock,
  type ApiWorkoutStructure,
} from '../../utils/workoutStructure';

function calendarStructureBlocks(structure: unknown | null): ApiStructBlock[] {
  if (!structure || typeof structure !== 'object') return [];
  const blocks = (structure as ApiWorkoutStructure).structure;
  return Array.isArray(blocks) ? blocks : [];
}

/** Proportional structure preview — same as calendar workout cards. */
export function CalendarWorkoutStructureBar({ structure }: { structure: unknown | null }) {
  const blocks = calendarStructureBlocks(structure);
  if (!blocks.length) return null;

  const steps = collapseStructurePreview(blocks);
  if (!steps.length) return null;

  const totalDur = steps.reduce((sum, step) => sum + stepLengthSecs(step.length), 0);
  if (!totalDur) return null;

  const scaleMax = Math.max(
    100,
    ...steps.map((step) => {
      const { min, max } = stepTargetRange(step);
      return (min + max) / 2;
    }),
  );

  const W = 100;
  const H = 20;
  let xCursor = 0;
  const bars: React.ReactNode[] = [];

  steps.forEach((step, i) => {
    const dur = stepLengthSecs(step.length);
    if (!dur) return;

    const w = Math.max((dur / totalDur) * W, 1.5);
    const { min, max } = stepTargetRange(step);
    const mid = (min + max) / 2;
    const barH = Math.max((mid / scaleMax) * H, 2);

    bars.push(
      <rect
        key={i}
        x={xCursor}
        y={H - barH}
        width={Math.max(w - 0.5, 1)}
        height={barH}
        data-training-zone={stepZoneNumber(step)}
        fill={stepZoneColor(step)}
        stroke="var(--training-zone-outline)" strokeWidth={0.4}
        style={{ fill: stepZoneColor(step) }}
        opacity={1}
        rx={1}
      />,
    );
    xCursor += w;
  });

  if (!bars.length) return null;

  return (
    <div className="ltr-island" dir="ltr" style={{ width: '100%', marginTop: 3 }}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
        aria-hidden
      >
        {bars}
      </svg>
    </div>
  );
}
